import { Box, Grid, Button, Typography, IconButton } from "@material-ui/core";
import { Form, Modal } from "@components";
import React, { useCallback, useContext, useState } from "react";
import { useHistory } from "react-router";
import { Actions } from "../../../../../../screens/request/components/actions";
import CloseIcon from "@material-ui/icons/Close";
import { Api } from "@api";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { Category } from "../../../../../../store";
import { FormData } from "./form";
import { alertService } from "@services";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

interface Props {
  id: 0;
  data: Category | undefined;
  isOpen: boolean;
  onClickAdd: () => void;
  onClickClose: () => void;
  action: string;
}

type FormValues = {
  id: number;
  name: string;
  isActive: boolean;
  isManual: boolean;
  businessUnit: {
    id: number;
    name: string;
  };
};

type Key = keyof FormValues;

export const CategoryDetails: React.FC<Props> = ({
  id,
  data,
  isOpen,
  onClickAdd,
  onClickClose,
  action,
}) => {
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState<
    boolean
  >(false);
  const [pending, setPending] = React.useState(false);
  const initial_values = data;
  const [values, setValues] = React.useState<FormValues | undefined>(
    initial_values
  );
  const nullSystemType = { id: 0, name: "" };
  const form = Form.useManager<FormValues>({
    id: 0,
    name: data ? data.name : "",
    isActive: data ? data.isActive : false,
    isManual: data ? data.isManual : false,
    businessUnit: data ? data.businessUnit : nullSystemType,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    console.log(name);
    switch (name) {
      case "name":
      case "businessUnit":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleAddButtonClick(): void {
    if (!form.values.businessUnit.id || !form.values.name) {
      alertService.danger({
        msg: "Please complete required field(s).",
      });
      return;
    }

    setPending(true);
    setIsSubmitting(true);
    Api.category
      .editCategory(data ? data.id : id, {
        ...form.values,
      })
      .then(() => {
        alertService.success({
          msg: "System category has been successfully saved.",
        });
        setPending(false);
        setIsOpenConfirmationModal(false);
        form.reset();
        onClickClose();
        onClickAdd();
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({ statusCode: error.response.status });
        }
        setPending(false);
        console.log("Error: Update System Category. " + error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  function handleSubmitButtonClick(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          handleAddButtonClick();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const clickCloseBtn = () => {
    setValues(undefined);
    form.reset();
    onClickClose();
  };

  const handleClose = () => {
    setOpen(false);
  };
  const styles = (theme: Theme) =>
    createStyles({
      root: {
        margin: 0,
        padding: theme.spacing(2),
      },
      closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
      },
    });

  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });
  const submitText = isSubmitting ? "Submitting..." : "Submit";
  return (
    <Box mt={2} mb={2}>
      <Modal
        isOpen={isOpenConfirmationModal}
        positionButton="center"
        actions={[
          {
            label: "Cancel",
            variant: "outlined",
            handler: () => {
              setIsOpenConfirmationModal(false);
            },
          },
          {
            label: pending ? "Submitting..." : "Submit",
            handler: () => {
              handleAddButtonClick();
            },
            disabled: pending,
          },
        ]}
      >
        <Typography align="center" paragraph>
          Are you sure you want to update this System Category?
        </Typography>
      </Modal>

      <Dialog
        open={isOpen}
        onClose={clickCloseBtn}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth={"sm"}
      >
        <DialogTitle id="form-dialog-title" onClose={clickCloseBtn}>
          Update System Category: {data ? data.name : ""}
        </DialogTitle>
        <DialogContent>
          <FormData data={data} onHandleChange={onChange} form={form} />
        </DialogContent>
        <DialogActions>
          <Button onClick={clickCloseBtn} color="primary">
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={handleSubmitButtonClick}
            color="primary"
          >
            {submitText}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
